/**
 * ListingProductSidebar
 *
 * Reusable sidebar to switch between My Listings (/my-listings) and
 * My Products (/my-products). Self-contained: manages its own open/close
 * state and renders the floating FAB button.
 *
 * Usage:
 *   <ListingProductSidebar activePage="listings" user={user} />
 *   <ListingProductSidebar activePage="products" user={user} />
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Drawer,
  Fab,
  Paper,
  Divider,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  keyframes
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountIcon,
  SwapHoriz as SwapIcon,
  ListAlt as ListingsIcon,
  Inventory2 as ProductsIcon,
  PauseCircle as PauseCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const gradientStyle = {
  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
  color: 'white'
};

const pulseAnimation = keyframes`
  0% {
    transform: translateY(-50%) scale(1);
    box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
  }
  50% {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 12px 25px rgba(98, 0, 238, 0.5);
  }
  100% {
    transform: translateY(-50%) scale(1);
    box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
  }
`;

/**
 * @param {object}  props
 * @param {'listings'|'products'|'activity-suspension'} props.activePage  - which page is currently displayed
 * @param {object}  props.user                       - current user from AuthContext
 */
const ListingProductSidebar = ({ activePage, user }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('products');

  const toggle = () => setOpen((prev) => !prev);
  const close  = () => setOpen(false);

  const items = [
    {
      title: t('sidebar.dashboard'),
      icon: <DashboardIcon />,
      description: t('sidebar.dashboardDesc'),
      action: () => { close(); navigate('/dashboard'); },
      active: false
    },
    { type: 'divider' },
    {
      title: t('sidebar.myListings'),
      icon: <ListingsIcon />,
      description: t('sidebar.listingsDesc'),
      action: () => { close(); navigate('/my-listings'); },
      active: activePage === 'listings'
    },
    {
      title: t('sidebar.myProducts'),
      icon: <ProductsIcon />,
      description: t('sidebar.productsDesc'),
      action: () => { close(); navigate('/my-products'); },
      active: activePage === 'products'
    },
    {
      title: t('sidebar.activitySuspension', 'Suspendre mon activité'),
      icon: <PauseCircleIcon />,
      description: t('sidebar.activitySuspensionDesc', 'Désactiver ou réactiver vos annonces et produits en masse'),
      action: () => { close(); navigate('/activity-suspension'); },
      active: activePage === 'activity-suspension'
    }
  ];

  const SidebarContent = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ ...gradientStyle, p: 3, display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
        <IconButton
          onClick={close}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          src={user?.avatar}
          sx={{ width: 48, height: 48, border: '2px solid white' }}
        >
          <AccountIcon />
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', lineHeight: 1.2 }}>
            {t('sidebar.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </Box>

      {/* Menu items */}
      <Box sx={{ flex: 1, p: 2 }}>
        {items.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={index} sx={{ my: 1 }} />;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              onClick={item.action}
              sx={{
                mb: 1,
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                backgroundColor: item.active
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                borderLeft: item.active
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2]
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: item.active
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: item.active ? 600 : 400,
                      color: item.active
                        ? theme.palette.primary.main
                        : theme.palette.text.primary
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapIcon />}
          onClick={() => { close(); navigate('/dashboard'); }}
          sx={{ borderRadius: 2 }}
        >
          {t('sidebar.backToHub')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', md: 350 },
            maxWidth: '100vw',
            height: '100%',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <SidebarContent />
      </Drawer>

      {/* FAB flottant */}
      {!open && (
        <Tooltip title={t('sidebar.title')} placement="left">
          <Fab
            color="primary"
            onClick={toggle}
            sx={{
              position: 'fixed',
              top: '50%',
              right: 24,
              transform: 'translateY(-50%)',
              zIndex: 1301,
              animation: `${pulseAnimation} 2s ease-in-out infinite`,
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              '&:hover': {
                animation: 'none',
                transform: 'translateY(-50%) scale(1.15)',
                boxShadow: '0 12px 25px rgba(98, 0, 238, 0.6)',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(45deg, #6200ee 40%, #03dac4 80%)'
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)',
                transition: 'all 0.1s ease'
              }
            }}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Tooltip>
      )}
    </>
  );
};

export default ListingProductSidebar;
